import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { CircularProgress } from '@mui/material'
import { useGetBusinessIfExists } from '@marketing-milk/frontend'
import { useWindowSize } from 'hooks/useWindowSize'
import { useSprintPacket } from 'hooks/api/useSprintPackets'
import { CommentAnchor, CommentThread } from 'components/comments/CommentThread'
import { AllCommentsDrawer } from 'components/comments/AllCommentsDrawer'
import { anchorSectionKey } from 'components/comments/groupCommentsByAnchor'
import { countUnresolvedTotal } from 'components/comments/commentCounts'
import { readCommentDeepLink } from 'components/comments/commentDeepLink'
import { EditorTOC } from './EditorTOC'
import { PacketHeader } from './PacketHeader'
import { useActiveSection } from './useActiveSection'
import { useCommentAnchors } from './useCommentAnchors'
import { UnresolvedCommentsProvider } from './UnresolvedCommentsContext'
import { SECTIONS } from './sections'
import { dark, light, radius, type } from '../tokens'
import { PerformanceReviewSection } from './sections/PerformanceReviewSection'
import { NarrativeSection } from './sections/NarrativeSection'
import { ClientSentimentSection } from './sections/ClientSentimentSection'
import { AudienceSection } from './sections/AudienceSection'
import { CreativeSection } from './sections/CreativeSection'
import { creativeWizardUrl } from '../../creatives/creativeUrl'
import { audiencesUrl } from '../../audiences/audienceUrl'

const HEADER_OFFSET = 60

interface Props {
  businessID: number
  packetID: number
}

export function PacketEditorBody({ businessID, packetID }: Props) {
  const { packet, isLoading, isError, refetch } = useSprintPacket(packetID)
  const business = useGetBusinessIfExists()
  const [windowHeight] = useWindowSize()
  const ready = !isLoading && !isError && !!packet
  const { anchor, open, close, setAnchor, setOpen, rootRef, setRoot } = useCommentAnchors()
  const activeKey = useActiveSection({ sections: SECTIONS, scrollRootRef: rootRef, ready })
  const handledDeepLinkRef = useRef(false)
  const [allCommentsOpen, setAllCommentsOpen] = useState(false)
  const [threadTargetCommentId, setThreadTargetCommentId] = useState<number | null>(null)

  // Navigate the editor to an anchor and open its thread. Scrolls to the entity
  // element when one is mounted (creative/audience cards), else to the owning
  // section via `anchorSectionKey`. Shared by the Slack deep-link effect and the
  // "All comments" list selection. `commentId` drives CommentThread's in-app
  // highlight; omit it for deep-links (CommentThread reads ?commentId= itself).
  const openAnchor = useCallback(
    (target: CommentAnchor, commentId?: number | null) => {
      const sectionKey = anchorSectionKey(target.type, target.id)
      const entityEl = document.querySelector<HTMLElement>(
        `[data-comment-anchor-type="${target.type}"][data-comment-anchor-id="${target.id}"]`
      )
      const el = entityEl ?? (sectionKey ? document.getElementById(sectionKey) : null)
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      setThreadTargetCommentId(commentId ?? null)
      setAnchor(target)
      setOpen(true)
    },
    [setAnchor, setOpen]
  )

  // Open the right thread when arriving via a Slack comment deep-link
  // (`?commentId=&anchorType=&anchorId=`). Packets have many anchors, so unlike
  // CreativeWizard we must set the anchor before opening the drawer; once open,
  // CommentThread's own `?commentId=` effect scrolls + highlights the comment.
  // The ref latch keeps this run-once so a later refetch can't reopen the drawer.
  useEffect(() => {
    if (!ready || handledDeepLinkRef.current) return
    const link = readCommentDeepLink()
    if (!link) return
    handledDeepLinkRef.current = true
    openAnchor({ type: link.anchorType, id: link.anchorId })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready])

  const closeThread = useCallback(() => {
    close()
    setThreadTargetCommentId(null)
  }, [close])

  const onSelectComment = useCallback(
    (target: CommentAnchor, commentId: number) => {
      setAllCommentsOpen(false)
      openAnchor(target, commentId)
    },
    [openAnchor]
  )

  const unresolvedTotal = useMemo(
    () => countUnresolvedTotal(packet?.comments ?? []),
    [packet?.comments]
  )
  // Dev-only: `?theme=dark` flips to the dark palette so v0.13 (PDF) and v0.14
  // (Present mode) can be visually validated through the real editor without
  // standing up Storybook. Defaults to `light` for AE-facing edit mode.
  const { search } = useLocation()
  const palette = new URLSearchParams(search).get('theme') === 'dark' ? dark : light

  const scrollHeight = windowHeight - HEADER_OFFSET

  return (
    <UnresolvedCommentsProvider comments={packet?.comments ?? []}>
      <div
        ref={setRoot}
        style={{
          height: scrollHeight,
          maxHeight: scrollHeight,
          background: palette.bg,
          color: palette.text,
          fontFamily: type.family.sans,
        }}
        className="w-full overflow-y-auto"
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <CircularProgress size={28} />
          </div>
        ) : isError || !packet ? (
          <div style={{ padding: 24 }}>
            <div
              style={{
                background: palette.surface,
                border: `1px solid ${palette.hairline}`,
                borderRadius: radius.lg,
                padding: 40,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 12,
                textAlign: 'center',
              }}
            >
              <h2
                style={{
                  margin: 0,
                  fontFamily: type.family.sans,
                  fontSize: type.size.h2,
                  fontWeight: type.weight.semibold,
                  color: palette.text,
                }}
              >
                Couldn't load this sprint packet
              </h2>
              <p style={{ fontSize: type.size.body, color: palette.textMuted, maxWidth: 320 }}>
                Something went wrong fetching the packet. Try again in a moment.
              </p>
              <button
                type="button"
                onClick={() => refetch()}
                style={{
                  padding: '8px 16px',
                  borderRadius: radius.sm,
                  border: `1px solid ${palette.hairline}`,
                  background: palette.surface,
                  color: palette.text,
                  fontFamily: type.family.mono,
                  fontSize: type.size.eyebrow,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                }}
              >
                Retry
              </button>
            </div>
          </div>
        ) : (
          <div className="flex w-full min-h-full">
            <EditorTOC
              palette={palette}
              activeKey={activeKey}
              onOpenAllComments={() => setAllCommentsOpen(true)}
              unresolvedTotal={unresolvedTotal}
            />
            <div className="flex-1 flex flex-col min-w-0">
              <PacketHeader
                palette={palette}
                businessName={business?.businessName}
                phase={business?.phase}
                sprint={packet.sprint}
              />
              <main className="px-6 py-4 space-y-6 w-full">
                {SECTIONS.map(s => {
                  switch (s.kind) {
                    case 'performance':
                      return (
                        <PerformanceReviewSection key={s.key} palette={palette} packet={packet} />
                      )
                    case 'narrative':
                      return (
                        <NarrativeSection
                          key={s.key}
                          palette={palette}
                          sectionKey={
                            s.key as Exclude<
                              typeof s.key,
                              | 'performance-review'
                              | 'client-sentiment'
                              | 'audience-framework'
                              | 'creative'
                            >
                          }
                          label={s.label}
                          packetID={packet.id}
                          narrative={packet.narrative}
                        />
                      )
                    case 'client-sentiment':
                      return (
                        <ClientSentimentSection
                          key={s.key}
                          palette={palette}
                          packetID={packet.id}
                          sentiment={packet.clientSentiment ?? null}
                        />
                      )
                    case 'audience':
                      return (
                        <AudienceSection
                          key={s.key}
                          palette={palette}
                          businessID={packet.businessID}
                          packetID={packet.id}
                          audiences={packet.audiences}
                          audienceSelection={packet.audienceSelection ?? null}
                        />
                      )
                    case 'creative':
                      return (
                        <CreativeSection
                          key={s.key}
                          palette={palette}
                          packetID={packet.id}
                          sprintID={packet.sprintID}
                        />
                      )
                  }
                })}
                <div className="h-[60vh]" aria-hidden />
              </main>
            </div>
          </div>
        )}
      </div>
      {packet && (
        <AllCommentsDrawer
          open={allCommentsOpen}
          onClose={() => setAllCommentsOpen(false)}
          palette={palette}
          comments={packet.comments}
          audiences={packet.audiences}
          onSelectComment={onSelectComment}
        />
      )}
      {packet && (
        <CommentThread
          open={open}
          onClose={closeThread}
          businessID={businessID}
          anchor={anchor}
          targetCommentId={threadTargetCommentId}
          // A comment mutation only invalidates the per-anchor comments query, not
          // the packet — refetch the packet so the hydrated comments (the badge
          // source) refresh and an anchor's unresolved badge clears on resolve.
          onCommentsChanged={refetch}
          notificationContext={{
            header: `Sprint Packet — ${packet.sprint?.name ?? `#${packet.sprintID}`}`,
            pageUrlBase: window.location.href.split('?')[0],
            pageUrlBaseForAnchor: a =>
              a.type === 'creative'
                ? creativeWizardUrl(businessID, a.id)
                : a.type === 'audience'
                ? audiencesUrl(businessID)
                : undefined,
            slackChannelId: business?.slackChannelId,
          }}
        />
      )}
    </UnresolvedCommentsProvider>
  )
}
